import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import type { ICertificateCruiseLine } from '@share/common-types';

import DefaultCruiseLineLogo from '@assets/images/certificates/cruise.png';

import { CRUISE_LINES_INITIAL_VISIBLE_COUNT } from '../../constants';
import type { DetailEligibleCruiseLinesProps } from './types';

import styles from './styles.module.scss';

interface CruiseLineEntryProps {
  line: ICertificateCruiseLine;
}

function CruiseLineEntry({ line }: CruiseLineEntryProps): React.ReactElement {
  const [hasLogoError, setHasLogoError] = useState(false);
  const trimmedLogoUrl = line.logoUrl?.trim() ?? '';
  const logoSrc = trimmedLogoUrl && !hasLogoError ? trimmedLogoUrl : DefaultCruiseLineLogo;

  const handleLogoError = useCallback(() => {
    if (trimmedLogoUrl) {
      setHasLogoError(true);
    }
  }, [trimmedLogoUrl]);

  return (
    <li className={styles.cruiseLineItem}>
      <div className={styles.cruiseLineLogoWrap}>
        <img
          className={styles.cruiseLineLogo}
          src={logoSrc}
          alt=""
          loading="lazy"
          onError={handleLogoError}
        />
      </div>
      <span className={styles.cruiseLineName}>{line.name}</span>
    </li>
  );
}

export const DetailEligibleCruiseLines: React.FC<DetailEligibleCruiseLinesProps> = ({
  cruiseLines,
}) => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);

  const isExpandable = cruiseLines.length > CRUISE_LINES_INITIAL_VISIBLE_COUNT;
  const visibleCruiseLines = useMemo(() => {
    if (!isExpandable || isExpanded) {
      return cruiseLines;
    }
    return cruiseLines.slice(0, CRUISE_LINES_INITIAL_VISIBLE_COUNT);
  }, [cruiseLines, isExpandable, isExpanded]);

  const handleToggleExpanded = useCallback((): void => {
    setIsExpanded((prev) => !prev);
  }, []);

  return (
    <section className={styles.section} aria-labelledby="eligible-cruise-heading">
      <div className={styles.sectionHeader}>
        <h2 id="eligible-cruise-heading" className={styles.sectionTitle}>
          {intl.formatMessage({ id: 'certificates.detail.eligibleCruiseHeading' })}
        </h2>
        {isExpandable && (
          <button
            type="button"
            className={styles.showAllButton}
            onClick={handleToggleExpanded}
            aria-expanded={isExpanded}
            aria-controls="eligible-cruise-lines-grid"
          >
            {isExpanded
              ? intl.formatMessage({ id: 'certificates.detail.showLess' })
              : intl.formatMessage({ id: 'certificates.detail.showAll' })}
          </button>
        )}
      </div>
      <ul id="eligible-cruise-lines-grid" className={styles.cruiseLinesGrid}>
        {visibleCruiseLines.map((line) => (
          <CruiseLineEntry key={String(line.cruiseLineId ?? line.id)} line={line} />
        ))}
      </ul>
    </section>
  );
};
